import { IconButton, Tooltip, Typography, useTheme } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import {
  forwardRef,
  PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import AttachFile from "@mui/icons-material/AttachFile";
import { defineMessages, useIntl } from "react-intl";

const messages = defineMessages({
  files: {
    id: "files.files",
    defaultMessage: "Specific Knowledge",
  },
  tooltip: {
    id: "files.tooltip",
    defaultMessage:
      "Documents are loaded to the Assistant and permanently considered to answer the user question.",
  },
});

export type FileWithPreview = File & {
  id?: string;
  preview?: string;
};

type FilePreviewProps = {
  text?: string;
  setText?: (text: React.SetStateAction<string | undefined>) => void;
  files: FileWithPreview[];
  setFiles: (value: React.SetStateAction<FileWithPreview[]>) => void;
  addFile: (file: File) => void;
  deleteFile: (file: File) => void;
  disabled?: boolean;
};

type ButtonProps = {
  ref: React.RefObject<HTMLInputElement>;
  id?: string;
  disabled?: boolean;
  accept: string;
  onChange: React.ChangeEventHandler<HTMLInputElement>;
  clear: () => void;
};

type DropzoneProps = {
  accept?: string;
  setFiles: (value: React.SetStateAction<FileWithPreview[]>) => void;
  setText?: (text: string) => void;
  isGlobal?: boolean;
};

export type FileUploadProps = {
  previewProps: FilePreviewProps;
  buttonProps: ButtonProps;
  dropzoneProps: DropzoneProps;
};

export function useFileUploadProps({
  isGlobal = false,
}: { isGlobal?: boolean } = {}): FileUploadProps {
  const [files, setFiles] = useState<FileWithPreview[]>([]);
  const [text, setText] = useState<string | undefined>(undefined);
  const ref = useRef<HTMLInputElement>(null);

  const clear = () => {
    files.forEach((file) => {
      if (file.preview) {
        URL.revokeObjectURL(file.preview);
      }
    });
    setText(undefined);
    setFiles([]);
    if (ref.current) {
      ref.current.value = "";
    }
  };

  const addFile = (file: File) => {
    setFiles((files) => [
      ...files,
      Object.assign(file, {
        preview: URL.createObjectURL(file),
      }),
    ]);
  };

  const onChange: React.ChangeEventHandler<HTMLInputElement> | undefined = (
    event
  ) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }
    addFile(file);
  };

  const deleteFile = (file: File) => {
    setFiles((files) => {
      const _files: FileWithPreview[] = [];
      files.forEach((f) => {
        if (f.name === file.name && f.preview) {
          URL.revokeObjectURL(f.preview);
        }
        if (f.name !== file.name) {
          _files.push(f);
        }
      });
      return _files;
    });
    if (ref.current) {
      ref.current.value = "";
    }
  };

  const accept =
    "application/pdf,text/csv,text/xml,application/vnd.oasis.opendocument.text,text/plain,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.openxmlformats-officedocument.presentationml.presentation,application/json,image/jpeg,image/png,application/msword,application/vnd.ms-powerpoint,application/vnd.ms-excel,application/vnd.ms-outlook,message/rfc822,application/rtf,audio/mpeg,audio/mp3,audio/wav,video/mp4,video/mpeg,.twb,.twbx,.twc,.md";

  return {
    previewProps: {
      text,
      files,
      setFiles,
      setText,
      addFile,
      deleteFile,
    },
    buttonProps: {
      ref,
      accept,
      onChange,
      clear,
    },
    dropzoneProps: {
      accept,
      setFiles,
      setText,
      isGlobal,
    },
  };
}

export function FilesHeading() {
  const { formatMessage: t } = useIntl();
  return (
    <div className="flex items-center">
      <Typography variant="h6" id="input-slider">
        {t(messages.files)}
      </Typography>
      &nbsp;
      <Tooltip title={t(messages.tooltip)}>
        <HelpOutlineIcon fontSize="small" />
      </Tooltip>
    </div>
  );
}

export function FilesDropzone({
  children,
  accept,
  setFiles,
  setText,
  isGlobal = false,
}: PropsWithChildren<DropzoneProps>) {
  const addFiles = useCallback(
    (files: FileWithPreview[]) => {
      setFiles((_files) => {
        const existingFiles = _files.map((file) => file.name);
        const filteredFiles = files.filter(
          (file) => !existingFiles.includes(file.name)
        );
        return [..._files, ...filteredFiles];
      });
    },
    [setFiles]
  );
  const acceptEntries = useMemo(
    () => accept?.split(",").map((type) => type.trim()) ?? [],
    [accept]
  );
  const mimeTypeSupported = useCallback(
    (mimeType: string) => {
      return acceptEntries.length ? acceptEntries.includes(mimeType) : true;
    },
    [acceptEntries]
  );
  const extensionSupported = useCallback(
    (fileName: string) => {
      const ext = "." + (fileName.split(".").pop()?.toLowerCase() ?? "");
      return acceptEntries.some((entry) => entry.startsWith(".") && entry === ext);
    },
    [acceptEntries]
  );
  const handleDrop = useCallback(
    (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();

      if (
        e.dataTransfer &&
        e.dataTransfer.files &&
        e.dataTransfer.files.length > 0
      ) {
        const filesWithPreview = Array.from(e.dataTransfer.files ?? [])
          .map((file: File) =>
            Object.assign(file, {
              preview: URL.createObjectURL(file),
            })
          )
          .filter((file) => mimeTypeSupported(file.type) || extensionSupported(file.name));
        addFiles(filesWithPreview);
      }
    },
    [addFiles, mimeTypeSupported, extensionSupported]
  );

  const isMounted = useRef(true);
  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  const handlePaste = useCallback(
    (e: ClipboardEvent) => {
      const target = e.target as HTMLElement;
      const isEditableElement =
        target &&
        (target.isContentEditable ||
          ["textarea", "input"].includes(target.tagName.toLowerCase()));

      // Check if clipboard contains files
      const items = e?.clipboardData?.items;
      if (!items) return;

      const hasFiles = Array.from(items).some((c) => c.kind === "file");

      if (isEditableElement && !hasFiles) return;

      e.preventDefault();
      e.stopPropagation();

      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        if (item.kind === "file") {
          const file = item.getAsFile();
          // Accept by mime type or, for types the browser leaves blank (e.g. .md),
          // by file extension — matching the drag-and-drop behaviour above.
          if (
            file &&
            (mimeTypeSupported(item.type) || extensionSupported(file.name))
          ) {
            addFiles([
              Object.assign(file, { preview: URL.createObjectURL(file) }),
            ]);
          }
        }
        // Handle text items (e.g., pasted text)
        else if (item.kind === "string" && item.type === "text/plain") {
          item.getAsString((text) => isMounted.current && setText?.(text));
        }
      }
    },
    [addFiles, mimeTypeSupported, extensionSupported, setText]
  );

  const handleDragOver = useCallback((e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer) {
      e.dataTransfer.dropEffect = "copy";
    }
  }, []);

  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const element = isGlobal
      ? document.querySelector<HTMLDivElement>("#root")
      : ref.current;

    if (!element) return;

    element.addEventListener("paste", handlePaste);
    element.addEventListener("dragover", handleDragOver);
    element.addEventListener("drop", handleDrop);

    return () => {
      element.removeEventListener("paste", handlePaste);
      element.removeEventListener("dragover", handleDragOver);
      element.removeEventListener("drop", handleDrop);
    };
  }, [handleDragOver, handleDrop, handlePaste, isGlobal]);

  return (
    <div id="dropzone" className="flex flex-col flex-1" ref={ref}>
      {children}
    </div>
  );
}

export function FilesPreview({ files, deleteFile, disabled }: FilePreviewProps) {
  const theme = useTheme();
  const isImage = (file: File) => {
    return file.type.startsWith("image/");
  };
  return (
    <>
      {files.map((file) => (
        <div
          key={file.name}
          className="relative inline-flex border-2 border-dashed rounded-xl overflow-hidden ml-2 mb-2 h-28 min-w-[50px]"
        >
          <div
            className="absolute top-0 right-0 rounded-2xl"
            style={{
              backgroundColor: theme.palette.background.default,
            }}
          >
            <IconButton size="small" onClick={deleteFile.bind(null, file)} disabled={disabled}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </div>
          {isImage(file) ? (
            <img
              className="block w-auto h-full"
              src={file.preview}
            />
          ) : (
            <div
              className="p-2 flex items-center justify-center min-w-[100px]"
              style={{
                backgroundColor: theme.palette.background.default,
              }}
            >
              <Typography variant="caption" className="font-bold">
                {file.name}
              </Typography>
            </div>
          )}
        </div>
      ))}
    </>
  );
}

export const FileUploadButton = forwardRef<
  HTMLInputElement,
  Omit<ButtonProps, "ref">
>(function FileUploadButtonInner(
  {
    id = "attachment-upload",
    disabled = false,
    onChange,
    accept = "application/pdf,text/csv,text/xml,application/vnd.oasis.opendocument.text,text/plain,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.openxmlformats-officedocument.presentationml.presentation,application/json,image/jpeg,image/png,application/msword,application/vnd.ms-powerpoint,application/vnd.ms-excel,application/vnd.ms-outlook,message/rfc822,application/rtf,audio/mpeg,audio/mp3,audio/wav,video/mp4,video/mpeg,.twb,.twbx,.twc,.md",
  },
  ref
) {
  return (
    <Tooltip title="Upload Files">
      <label htmlFor={id}>
        <input
          ref={ref}
          hidden
          id={id}
          type="file"
          multiple
          onChange={onChange}
          accept={accept}
          disabled={disabled}
        />
        <IconButton
          aria-label="upload picture"
          component="span"
          size="small"
          disabled={disabled}
        >
          <AttachFile fontSize="small" />
        </IconButton>
      </label>
    </Tooltip>
  );
});
